import type {
  FilterGroup,
  FilterGroupListItem,
} from '@kizen/api/custom-object';
import type { FilterSet } from '@kizen/filters/filter-sets';
import type { FilterConfig } from '@kizen/filters/load';
import type { FilterVariables } from '@kizen/filters/variables';
import type { FilterData } from '@kizen/filters/hooks/use-filter-sets';
import type { ResourceOf } from '@kizen/query/resource';
import type { PropsWithChildren } from 'react';
import type { UseFilterVariableOptions } from 'ts-components/filters';

import {
  createFilterGroup,
  deleteFilterGroup,
  updateFilterGroup,
} from '@kizen/api/custom-object';
import { useFilterSets } from '@kizen/filters/hooks/use-filter-sets';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import {
  Fragment,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Collapse from 'react-bootstrap/Collapse';
import Overlay from 'react-bootstrap/Overlay';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { KizenTypography } from '__app/typography';
import { validate as validateEmail } from 'components/Inputs/TextInput/presets/EmailAddress';
import { useTruncationTooltip } from '__components/Kizen/Tooltip';
import {
  PageMainSelector,
  PageToolbarFiltersButton,
} from '__components/Layout/PageToolbar';
import ConfirmationModal from 'components/Modals/presets/ConfirmationModal';
import { useFlashTransition } from 'hooks/useFlashState';
import { customObjectFilterGroupsQuery } from 'query/custom-object';
import AxiosService from 'services/AxiosService';
import { selectFilterMetadataDefinition } from 'store/filterMetaData/selectors';
import { loadSavedFilter } from 'ts-filters/utils';
import {
  AddFilterSetButton,
  FilterSetOperationDropdown,
} from './AddFilterSetButton';
import { Filter } from './Filter';
import {
  AddFilterButton,
  ErrorCard,
  FilterControlButton,
  FilterControls,
  FilterNameInput,
  FilterNameWrapper,
  FilterMenu,
  FilterMenuHeader,
  FilterOperatorText,
  FilterRow,
  FilterSetContainer,
} from './components';
import { useFilterErrors } from './hooks/useFilterErrors';
import { useFilterVariables } from './hooks/useFilterVariables';

interface FilterSetsProviderProps
  extends Omit<UseFilterVariableOptions, 'instance' | 'objectId'> {
  config?: FilterConfig | Record<string, never>;
  groupId?: string | null;
  instance: FilterVariables['instance'];
  objectId: FilterVariables['custom_object_id'];
  onApplyFilters(data: {
    config: FilterConfig;
    group: FilterGroupListItem | null;
  }): void;
  onClearFilter(): void;
  onDeleteGroup(): void;
  onGroupChange(group: FilterGroupListItem | null): void;
  onSaveGroup(group: FilterGroupListItem): void;
}

interface FilterSetsContext extends ReturnType<typeof useLoadFilterSets> {
  groupsQueryKey: ResourceOf<ReturnType<typeof customObjectFilterGroupsQuery>>;
}

type FilterSetsBuilderContext = ReturnType<typeof useFilterSetsBuilder>;

const FilterSetsContext = createContext<
  FilterSetsContext | { uninitialized: true }
>({ uninitialized: true });

const FilterSetsBuilderContext = createContext<FilterSetsBuilderContext>(
  {} as FilterSetsBuilderContext
);

const isFilterConfig = (config: any): config is FilterConfig => {
  return config && typeof config === 'object' && 'query' in config;
};

const toFilterGroupListItem = (fg: FilterGroup): FilterGroupListItem => {
  const { config, created, id, name } = fg;

  return { config, created, id, name };
};

const getDefaultGroupLabels = (
  t: (x: string) => string
): Record<FilterVariables['instance'], string> => {
  return {
    automations_overview: '__unimplemented__',
    automations_condition_step: '__unimplemented__',
    broadcasts_schedule: '__unimplemented__',
    dashlets_dashboards: '__unimplemented__',
    dashlets_homepages: '__unimplemented__',
    page_builder_dynamic_images: '__unimplemented__',
    page_builder_dynamic_text: '__unimplemented__',
    record_details_related_object_block: '__unimplemented__',
    record_overview_contacts: '__unimplemented__',
    record_overview_custom_objects: '__unimplemented__',
    record_overview_team_member: t('All Team Members'),
  };
};

const FilterSetsProvider = (
  props: PropsWithChildren<FilterSetsProviderProps>
) => {
  const { children, ...rest } = props;

  const ctx = useLoadFilterSets(rest);

  return (
    <FilterSetsContext.Provider value={ctx}>
      {children}
    </FilterSetsContext.Provider>
  );
};

const BuilderProvider = (props: PropsWithChildren<FilterSetsContext>) => {
  const { children, ...rest } = props;

  const ctx = useFilterSetsBuilder(rest);

  return (
    <FilterSetsBuilderContext.Provider value={ctx}>
      {children}
    </FilterSetsBuilderContext.Provider>
  );
};

const useLoadFilterSets = (props: FilterSetsProviderProps) => {
  const {
    config: initialConfig,
    instance,
    fieldSettingsSearch,
    groupId,
    noObjectContext,
    variableSources,
    variableValues,
    objectId,
    onApplyFilters,
    onClearFilter,
    onDeleteGroup,
    onGroupChange,
    onSaveGroup,
  } = props;

  const qc = useQueryClient();
  const metadata = useSelector(selectFilterMetadataDefinition);
  const loadingRef = useRef(true);
  const [name, setName] = useState('');
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [group, setGroup] = useState<FilterGroupListItem | null>(null);
  const [showIconError, setShowIconError] = useState(false);
  const [filters, setFilters] = useState<FilterSet | FilterSet[]>();
  const [filterCount, setFilterCount] = useState(0);
  const [key, setKey] = useState(uuid());
  const [config, setConfig] = useState<FilterConfig | null>(
    isFilterConfig(initialConfig) ? initialConfig : null
  );

  const { variables: entries, isLoading: loadingVariables } =
    useFilterVariables({
      fieldSettingsSearch,
      instance,
      noObjectContext,
      variableSources,
      variableValues,
      objectId,
    });

  const variables = useMemo(() => {
    const record = Object.fromEntries(entries) as FilterVariables;

    return { entries, record };
  }, [entries]);

  const groupsQueryOptions = customObjectFilterGroupsQuery(objectId, {
    params: { type: 'visible' },
    staleTime: Infinity,
  });

  const groupsQuery = useQuery(groupsQueryOptions);

  const { data: groups = [], isLoading: loadingGroups } = groupsQuery;

  const loadGroup = Boolean(groupId);
  const loadConfig = !loadGroup && isFilterConfig(initialConfig);
  const dataReady = !loadingVariables && (!loadGroup || !loadingGroups);

  useEffect(() => {
    const onLoadFinish = () => {
      loadingRef.current = false;
      setKey(uuid());
    };

    const load = async (fc: FilterConfig) => {
      try {
        const res = await loadSavedFilter(metadata, fc, variables.record, qc);

        setFilters(res);
      } finally {
        onLoadFinish();
      }
    };

    if (!dataReady || !loadingRef.current) {
      return;
    }

    if (loadGroup) {
      const grp = groups.find((g) => g.id === groupId);

      if (grp) {
        // Still load the config if it is present. This is a valid runtime state. You should
        // be able to select a group, update the filters, navigate to a different page, and
        // then come back to the filters and save the group you were updating.
        if (isFilterConfig(initialConfig)) {
          load(initialConfig);
        } else {
          load(grp.config);
        }

        setGroup(grp);
        setName(grp.name);
      } else {
        onLoadFinish();
      }
    } else if (loadConfig) {
      load(initialConfig);
    } else {
      onLoadFinish();
    }
  }, [
    qc,
    metadata,
    initialConfig,
    variables,
    groups,
    groupId,
    loadConfig,
    loadGroup,
    dataReady,
  ]);

  const handleGroupChange = async (
    fg: FilterGroupListItem | null,
    { loadConfig = true } = {}
  ) => {
    setShowIconError(false);

    if (!fg) {
      setGroup(null);
      setName('');
      setFilters(undefined);
      setConfig(null);
      setKey(uuid());
      onGroupChange(null);
    } else {
      if (loadConfig) {
        const res = await loadSavedFilter(
          metadata,
          fg.config,
          variables.record,
          qc
        );

        setFilters(res);
        setConfig(fg.config);
        setKey(uuid());
      }

      setGroup(fg);
      setName(fg.name);
      onGroupChange(fg);
    }
  };

  return {
    config,
    instance,
    isLoading: loadingRef.current,
    key,
    filterCount,
    filters,
    group,
    groups,
    groupsQueryKey: groupsQueryOptions.queryKey,
    isBuilderOpen,
    name,
    objectId,
    showIconError,
    variables,
    handleGroupChange,
    setIsBuilderOpen,
    setGroup,
    setName,
    setConfig,
    setShowIconError,
    setFilterCount,
    onApplyFilters,
    onClearFilter,
    onDeleteGroup,
    onSaveGroup,
  };
};

const useFilterSetsBuilder = (props: FilterSetsContext) => {
  const {
    config,
    isBuilderOpen,
    filters,
    group,
    groupsQueryKey,
    name,
    objectId,
    variables,
    handleGroupChange,
    onApplyFilters,
    onClearFilter,
    onDeleteGroup,
    onSaveGroup,
    setConfig,
    setGroup,
    setIsBuilderOpen,
    setName,
    setShowIconError,
    setFilterCount,
  } = props;

  const { t } = useTranslation();
  const qc = useQueryClient();
  const metadata = useSelector(selectFilterMetadataDefinition);
  const [operation, setOperation] = useState<'and' | 'or'>(
    config?.and ? 'and' : 'or'
  );
  const [groupError, showGroupError, flashGroupError] = useFlashTransition();
  const [filterErrors, setFilterErrors] = useFilterErrors();
  const [filterSets, meta] = useFilterSets(
    metadata,
    variables.entries,
    isValidPhoneNumber,
    validateEmail.withDomain,
    filters
  );
  const {
    filterCount,
    hasAnyValidFiltersTypes,
    hasMyRecordFilter,
    hasStageRecordFilter,
    hasVariableValue,
    ...ops
  } = meta;

  const validatorRef = useRef(ops.validate);
  validatorRef.current = ops.validate;

  useEffect(() => {
    if (filters) {
      const { errors, hasErrors } = validatorRef.current();

      if (hasErrors) {
        setFilterErrors(errors);
        setIsBuilderOpen(true);
      }

      setShowIconError(hasErrors);
    }
  }, [filters, setFilterErrors, setIsBuilderOpen, setShowIconError]);

  useEffect(() => {
    setFilterCount(filterCount);
  }, [filterCount, setFilterCount]);

  const handleFilterChange = (data: FilterData, _step: string, _value: any) => {
    data.ops.update();
    setShowIconError(false);
  };

  const getErrorsForFilter = (options: { index: number; setId: string }) => {
    const { index, setId } = options;

    return filterErrors[setId]?.[index] ?? {};
  };

  const handleDeleteFilter = (options: {
    setId: string;
    index: number;
    isLastFilter: boolean;
  }) => {
    const { index, isLastFilter, setId } = options;

    if (!isLastFilter) {
      ops.removeFilter(setId, index);
    } else {
      ops.reset();
    }
  };

  const handleDeleteGroup = async () => {
    if (group) {
      await deleteFilterGroup(AxiosService, objectId, group.id);

      qc.setQueryData<FilterGroupListItem[]>(groupsQueryKey, (data) => {
        if (data) {
          return data.filter((g) => g.id !== group.id);
        }
      });

      handleGroupChange(null);
      setIsBuilderOpen(false);
      onDeleteGroup();
    }
  };

  const handleSaveGroup = async ({ createNewGroup = false } = {}) => {
    if (!name) {
      flashGroupError(t('Please type a group name before saving.'));
    } else {
      const { errors, hasErrors } = ops.validate();

      if (hasErrors) {
        setFilterErrors(errors);
      } else {
        const config = ops.build(operation === 'and');

        if (group && !createNewGroup) {
          const res = await updateFilterGroup(
            AxiosService,
            objectId,
            group.id,
            { config, name }
          );

          const groupListItem = toFilterGroupListItem(res);

          qc.setQueryData<FilterGroupListItem[]>(groupsQueryKey, (data) => {
            if (data) {
              const index = data.findIndex((g) => g.id === res.id);
              const next = data.toSpliced(index, 1, groupListItem);
              const sorted = next.sort((a, b) => (a.name < b.name ? -1 : 1));
              return sorted;
            }
          });

          setGroup(groupListItem);
          onSaveGroup(groupListItem);
        } else {
          const data = {
            config,
            name,
            sharing_settings: {
              private: true,
              all_team_members: 0 as const,
              roles: { admin: [], edit: [], view: [] },
              team_members: { admin: [], edit: [], view: [] },
            },
          };

          const res = await createFilterGroup(AxiosService, objectId, data);

          if (!res.ok) {
            if (res.error.errors.name) {
              flashGroupError(res.error.errors.name[0]);
            }

            return;
          }

          const groupListItem = toFilterGroupListItem(res.data);

          qc.setQueryData<FilterGroupListItem[]>(groupsQueryKey, (data) => {
            if (data) {
              return data.concat(groupListItem);
            }
          });

          handleGroupChange(groupListItem, { loadConfig: false });
          onSaveGroup(groupListItem);
        }

        setConfig(config);
      }
    }
  };

  const handleApplyFilter = () => {
    const { errors, hasErrors } = ops.validate({
      isEmptyFilterTypeInvalid: false,
    });

    if (hasErrors) {
      setFilterErrors(errors);
    } else {
      const config = ops.build(operation === 'and');
      setConfig(config);
      setShowIconError(false)
      onApplyFilters({ config, group });
    }
  };

  const handleClearFilter = () => {
    ops.reset();
    setConfig(null);
    setGroup(null);
    setIsBuilderOpen(false);
    onClearFilter();
  };

  return {
    filterSets,
    groupError,
    group,
    isBuilderOpen,
    name,
    operation,
    ops,
    showGroupError,
    getErrorsForFilter,
    handleApplyFilter,
    handleClearFilter,
    handleDeleteFilter,
    handleDeleteGroup,
    handleFilterChange,
    handleSaveGroup,
    setName,
    setOperation,
  };
};

const FilterSetsBuilder = forwardRef<HTMLDivElement>((_props, ref) => {
  const { t } = useTranslation();
  const ctx = useContext(FilterSetsContext);
  const builder = useContext(FilterSetsBuilderContext);
  const filterNameRef = useRef<HTMLInputElement>(null);
  const [isDeleteAction, setDeleteAction] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [nameTooltipProps, nameTooltip] = useTruncationTooltip({
    label: builder.name,
  });

  const {
    filterSets,
    groupError,
    group,
    isBuilderOpen,
    name,
    operation,
    ops,
    showGroupError,
    getErrorsForFilter,
    handleApplyFilter,
    handleClearFilter,
    handleDeleteFilter,
    handleDeleteGroup,
    handleFilterChange,
    handleSaveGroup,
    setName,
    setOperation,
  } = builder;

  if ('uninitialized' in ctx) {
    return null;
  }

  const { setShowIconError } = ctx;

  const multipleSets = filterSets.length > 1;
  const confirmMessage = isDeleteAction
    ? t('This will permanently delete the Filter Group.')
    : t('Your filters are unsaved, this will clear your work.');

  return (
    <>
      {/* @ts-expect-error - Collapse children type missing */}
      <Collapse mountOnEnter in={isBuilderOpen}>
        <div ref={ref}>
          <FilterMenu>
            <FilterMenuHeader>
              <FilterNameWrapper>
                <FilterNameInput
                  ref={filterNameRef}
                  placeholder={t('Enter Filter Group Name')}
                  value={name}
                  onChange={setName}
                  error={!!groupError}
                  debounce
                  {...nameTooltipProps}
                />
              </FilterNameWrapper>
              {name ? nameTooltip : null}
              {/* @ts-expect-error - Overlay children type missing */}
              <Overlay
                transition={false}
                target={filterNameRef.current!}
                show={groupError}
                placement="bottom-start"
              >
                <ErrorCard show={showGroupError} duration="300ms">
                  <KizenTypography>{groupError}</KizenTypography>
                </ErrorCard>
              </Overlay>
              <FilterControls>
                <FilterControlButton
                  variant="outline"
                  color="blue"
                  onClick={handleApplyFilter}
                >
                  {t('Apply Filter')}
                </FilterControlButton>
                <FilterControlButton
                  variant="outline"
                  color="green"
                  onClick={() => handleSaveGroup()}
                >
                  {t('Save Group')}
                </FilterControlButton>
                {group ? (
                  <>
                    <FilterControlButton
                      variant="outline"
                      color="green"
                      onClick={() => {
                        handleSaveGroup({ createNewGroup: true });
                      }}
                    >
                      {t('Create New Group')}
                    </FilterControlButton>
                    <FilterControlButton
                      variant="outline"
                      color="red"
                      onClick={() => {
                        setDeleteAction(true);
                        setShowConfirmationModal(true);
                      }}
                    >
                      {t('Delete Group')}
                    </FilterControlButton>
                  </>
                ) : null}
                <FilterControlButton
                  variant="outline"
                  color="red"
                  onClick={() => {
                    setDeleteAction(false);
                    setShowConfirmationModal(true);
                  }}
                >
                  {t('Clear All Filters')}
                </FilterControlButton>
              </FilterControls>
            </FilterMenuHeader>
            {filterSets.map(([setId, { and, filters }], setIdx) => {
              const multipleFilters = filters.length > 1;
              return (
                <Fragment key={setId}>
                  {setIdx > 0 && (
                    <FilterSetOperationDropdown
                      operation={operation}
                      onChange={setOperation}
                    />
                  )}
                  <FilterSetContainer
                    value={and ? 'all' : 'any'}
                    showBorder={multipleFilters || multipleSets}
                    onChange={(value) =>
                      ops.setFilterSetOperation(setId, value === 'all')
                    }
                  >
                    {filters.map((data, index, arr) => {
                      const { type, steps, id } = data;
                      const { next, set } = data.ops;
                      const isLastFilter = index === arr.length - 1;
                      const filterTypeChosen = type !== null;
                      const errors = getErrorsForFilter({ index, setId });

                      return (
                        <FilterRow key={id} data-qa="filter-row">
                          <Filter
                            showDeleteIcon
                            objectType="standard"
                            steps={steps}
                            next={next}
                            set={set}
                            errors={errors}
                            onChange={(step, value) => {
                              handleFilterChange(data, step, value);
                            }}
                            onDelete={() => {
                              handleDeleteFilter({
                                index,
                                isLastFilter: !multipleSets && !multipleFilters,
                                setId,
                              });
                            }}
                          />
                          {arr.length > 1 && !isLastFilter && (
                            <FilterOperatorText and={and} />
                          )}
                          {filterTypeChosen && isLastFilter && (
                            <AddFilterButton
                              onClick={() => ops.addFilter(setId)}
                            />
                          )}
                        </FilterRow>
                      );
                    })}
                  </FilterSetContainer>
                </Fragment>
              );
            })}
            <AddFilterSetButton
              topMargin={multipleSets || filterSets[0][1].filters.length > 1}
              onClick={(value) => {
                ops.addFilterSet();
                if (!multipleSets) {
                  setOperation(value);
                }
              }}
            />
          </FilterMenu>
        </div>
      </Collapse>
      {showConfirmationModal && (
        <ConfirmationModal
          data-qa="clear-confirm-modal"
          show={showConfirmationModal}
          buttonText={isDeleteAction ? t('Confirm Delete') : t('Confirm Clear')}
          heading={
            isDeleteAction ? t('Please Confirm Deletion') : t('Please Confirm')
          }
          onConfirm={() => {
            if (isDeleteAction) {
              handleDeleteGroup();
            } else {
              handleClearFilter();
            }
            setShowConfirmationModal(false);
            setShowIconError(false);
            setName('');
          }}
          onHide={() => {
            setDeleteAction(false);
            setShowConfirmationModal(false);
          }}
        >
          {confirmMessage}
        </ConfirmationModal>
      )}
    </>
  );
});

const ConnectedBuilder = forwardRef<HTMLDivElement>((_props, ref) => {
  const ctx = useContext(FilterSetsContext);

  if ('uninitialized' in ctx) {
    return null;
  }

  const { key, ...rest } = ctx;

  return (
    <BuilderProvider key={ctx.key} {...rest}>
      <FilterSetsBuilder ref={ref} />
    </BuilderProvider>
  );
});

interface FilterSetsIconButtonProps
  extends Pick<
    FilterSetsContext,
    | 'filterCount'
    | 'isBuilderOpen'
    | 'isLoading'
    | 'showIconError'
    | 'setIsBuilderOpen'
  > {
  onClick?(): void;
}

const FilterSetsIconButton = (props: FilterSetsIconButtonProps) => {
  const {
    filterCount,
    isBuilderOpen,
    isLoading,
    showIconError,
    onClick,
    setIsBuilderOpen,
  } = props;

  const handleClick = useCallback(() => {
    if (!isLoading) {
      setIsBuilderOpen((x) => !x);

      onClick?.();
    }
  }, [isLoading, onClick, setIsBuilderOpen]);

  return (
    <PageToolbarFiltersButton
      badge={filterCount}
      errors={showIconError}
      showBackground={isBuilderOpen}
      onClick={handleClick}
    />
  );
};

const ConnectedIconButton = (
  props: Pick<FilterSetsIconButtonProps, 'onClick'>
) => {
  const ctx = useContext(FilterSetsContext);

  if ('uninitialized' in ctx) {
    return null;
  }

  return (
    <FilterSetsIconButton
      filterCount={ctx.filterCount}
      isBuilderOpen={ctx.isBuilderOpen}
      isLoading={ctx.isLoading}
      showIconError={ctx.showIconError}
      onClick={props.onClick}
      setIsBuilderOpen={ctx.setIsBuilderOpen}
    />
  );
};

type Option<T> = { value: T; label: string };
type GroupOptions = [Option<'none'>, ...Option<FilterGroupListItem>[]];
type GroupOption = GroupOptions[number];

interface FilterSetsGroupSelectorProps
  extends Pick<
    FilterSetsContext,
    'instance' | 'isLoading' | 'group' | 'groups' | 'handleGroupChange'
  > {
  onChange?(value: FilterGroupListItem | null): void;
}

const FilterSetsGroupSelector = (props: FilterSetsGroupSelectorProps) => {
  const {
    instance,
    isLoading,
    group,
    groups = [],
    handleGroupChange,
    onChange,
  } = props;

  const { t } = useTranslation();

  const { options, lookup } = useMemo(() => {
    const defaultGroupLabels = getDefaultGroupLabels(t);

    const result: GroupOptions = [
      { value: 'none', label: defaultGroupLabels[instance] },
    ];

    if (isLoading) {
      return {
        options: result,
        lookup: {},
      };
    }

    result.push(
      ...groups.map((g) => {
        return { value: g, label: g.name };
      })
    );

    const record = result.reduce<Record<string, Option<FilterGroupListItem>>>(
      (acc, option) => {
        if (option.value !== 'none') {
          acc[option.value.id] = option;
        }

        return acc;
      },
      {}
    );

    return {
      options: result,
      lookup: record,
    };
  }, [groups, instance, isLoading, t]);

  const key = isLoading ? 'uninitialized' : 'initialized';

  return (
    <PageMainSelector
      key={key}
      value={group ? lookup[group.id] : options[0]}
      options={options}
      onChange={(option: GroupOption) => {
        const value = option.value === 'none' ? null : option.value;

        handleGroupChange(value);
        onChange?.(value);
      }}
    />
  );
};

const ConnectedGroupSelector = (
  props: Pick<FilterSetsGroupSelectorProps, 'onChange'>
) => {
  const ctx = useContext(FilterSetsContext);

  if ('uninitialized' in ctx) {
    return null;
  }

  return (
    <FilterSetsGroupSelector
      onChange={props.onChange}
      instance={ctx.instance}
      isLoading={ctx.isLoading}
      group={ctx.group}
      groups={ctx.groups}
      handleGroupChange={ctx.handleGroupChange}
    />
  );
};

interface UseFilterSetsContext
  extends Pick<FilterSetsContext, 'config' | 'group' | 'isLoading'> {}

export const useFilterSetsContext = (): UseFilterSetsContext => {
  const ctx = useContext(FilterSetsContext);

  if ('uninitialized' in ctx) {
    return { config: null, group: null, isLoading: true };
  }

  const { config, group, isLoading } = ctx;

  return { config, group, isLoading };
};

export const FilterSets = {
  Builder: ConnectedBuilder,
  GroupSelector: ConnectedGroupSelector,
  IconButton: ConnectedIconButton,
  Provider: FilterSetsProvider,
};
